import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import OrderStatusColor from '@/mixins/orders/orderStatus'
import OrderSourceType from '@/mixins/orders/sourceType'
import OrderPaymentType from '@/mixins/orders/paymentType'
import OrderType from '@/mixins/orders/orderType'
import OrderTypeTime from '@/mixins/orders/orderTypeTime'
import OrderChangeStatusDialog from '@/components/orders/OrderChangeStatusDialog.vue'
import OrderDeleteDialog from '@/components/orders/OrderDeleteDialog.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import Printer from '@/lib/print/printer.js'
import OrderStatusEnum from '@/api/Enums/OrderStatusEnum'
import SourceTypeEnum from '@/api/Enums/SourceTypeEnum'
import PinManagerEnum from '@/api/Enums/PinManagerEnum'
import OrderReceipts from '@/mixins/orders/orderReceipts'
import OrderTransactions from '@/mixins/orders/orderTransactions'
import {clone, getLocaleForReports} from '@/lib/helper/helper'
import * as SocketOrderHelper from '@/api/SocketOrderHelper.js'
import OrderEditActionEnum from '@/api/Enums/OrderEditActionEnum.js'
import SocketCommand from '@/api/SocketCommand.js'

export default {
  data() {
    return {
      search                               : '',
      loader                               : null,
      debounceSearchTimeout                : null,
      selectedOrder                        : null,
      selectedOrderWebhookHistory          : null,
      webhookHistoryDialogVisible          : false,
      cancelOrderConfirmDialogVisible      : false,
      revertOrderStatusConfirmDialogVisible: false,
      deleteOrderConfirmDialogVisible      : false,
      deleteAllOrdersConfirmDialogVisible  : false,
      orderPaymentDialogVisible            : false,
      confirmDialogEditOrderVisible        : false,
      isDeleteAndCopyOrder                 : false,
      deleteOrderLoading                   : false,
      selectedOrders                       : []
    }
  },
  components: {
    'order-change-status-dialog': OrderChangeStatusDialog,
    'order-delete-dialog'       : OrderDeleteDialog,
    'confirm-dialog'            : ConfirmDialog
  },
  mixins    : [Auth, AppData, OrderReceipts, OrderTransactions, OrderStatusColor, OrderSourceType, OrderPaymentType, OrderType, OrderTypeTime, OrderStatusEnum],
  props     : {},
  directives: {},
  watch     : {},
  methods   : {
    orderNotifyCustomerOrderStatus(order, from = null) {
      if (!order || order?.notifyStatusIsLoading) return

      this.executeWithPinPermissionManager(PinManagerEnum.PosOrderNotifications, (success) => {
        if (!success) return

        this.$set(order, 'notifyStatusIsLoading', true)
        window.callAS(window.SocketCommand.Order.StatusNotify, {OrderId: order.Id, OrderCode: order.OrderCode, From: from})
      })
    },

    orderNotifyCustomerOrderCollect(order, from = null) {
      if (!order || order?.notifyDeliveryIsLoading) return

      this.executeWithPinPermissionManager(PinManagerEnum.PosOrderNotifications, (success) => {
        if (!success) return

        this.$set(order, 'notifyDeliveryIsLoading', true)
        window.callAS(window.SocketCommand.Order.DeliveryNotify, {OrderId: order.Id, OrderCode: order.OrderCode, From: from})
      })
    },

    canUpdateOrderTime(order) {
      const rules = (this.appConfig.LOCATION_DATA.OrderTimeUpdate || []).filter(rule => !rule.disabled && rule.active)

      if (!order || order?.loading || order?.IsScheduled || !this.posUserHasComponentPermission('Orders', 'MANAGE')) return false
      if ((parseInt(order.StatusId) === OrderStatusEnum.NotComplete || parseInt(order.StatusId) === OrderStatusEnum.Received)) return true

      const validSourceTypes = [SourceTypeEnum.ALL, SourceTypeEnum.POS_GROUP, SourceTypeEnum.ONLINE_GROUP, SourceTypeEnum.EXTERNAL_GROUP, ...SourceTypeEnum.POS_SOURCES, ...SourceTypeEnum.ONLINE_SOURCES, ...SourceTypeEnum.EXTERNAL_PLATFORMS]
      const validStatuses = [0, OrderStatusEnum.Received, OrderStatusEnum.Accepted, OrderStatusEnum.Preparation, OrderStatusEnum.Delivery]

      return rules.some(rule =>
        (
          (order.SourceTypeId === rule.source && validSourceTypes.includes(order.SourceTypeId)) ||
          (validSourceTypes.includes(rule.source) && validSourceTypes.includes(order.SourceTypeId))
        ) &&
        (
          (order.StatusId === rule.status && validStatuses.includes(order.StatusId)) ||
          (rule.status === 0 && validStatuses.includes(order.StatusId))
        )
      )
    },

    onWebhookUpdateData(payload) {
      const command = window.SocketCommand.Order.Webhook.Get
      const payloadCommandsArray = payload?.command ? Array.isArray(payload?.command) ? payload.command : [payload.command] : []

      if (payloadCommandsArray.length) {
        payloadCommandsArray.forEach(cmd => {
          const payloadCommand = window.SocketCommand.commandToClientFormat(cmd || '')
          if (command === payloadCommand) {
            if (this.posUserCan('ORDERS', 'ACCESS') && (this.userIsSuperAdmin || this.userIsOwner)) {
              if (this.$route.name === 'OrdersToday' && payload?.action === 'update' && payload?.order_id) {
                SocketOrderHelper.handleOrderUpdate(command, payload)
              } else if (this.$route.name === 'OrdersAll') {
                this.getData()
              }

              if (this.webhookHistoryDialogVisible) {
                this.getOrderWebhookHistory(this.selectedOrder)
              }
            }
          }
        })
      }
    },

    getOrderWebhookHistory(order) {
      if (!this.posUserCan('ORDERS', 'ACCESS') || !this.userIsSuperAdmin || !order) return

      this.selectedOrder = order
      window.callAS(window.SocketCommand.Order.Webhook.History, {OrderId: order.Id})
    },
    getOrderWebhookHistoryResponse(data) {
      this.selectedOrderWebhookHistory = {
        History: data || [],
        Order  : this.selectedOrder
      }

      this.webhookHistoryDialogVisible = true
    },

    orderWebhookRetry(data) {
      if (!this.posUserCan('ORDERS', 'MANAGE') || !this.userIsSuperAdmin || !data?.Order) return

      this.selectedOrder = data.Order
      window.callAS(window.SocketCommand.Order.Webhook.Retry, {OrderId: data.Order.Id, RetryOnce: data.RetryOnce})
    },
    orderWebhookRetryResponse(data) {
      if (data && data.status === 'success') {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Order.OrderWebhookHistoryDialog.Message.Retry.Success'),
          type: 'success',
          icon: 'check'
        })

      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Order.OrderWebhookHistoryDialog.Message.Retry.Error'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    getOrderWebhookStatus(order) {
      const webhook = order?.Webhook
      const langObj = webhook?.Status ? webhook?.Successful ? this.$t('Order.OrdersAll.ListView.WebhookStatus.Success') : this.$t('Order.OrdersAll.ListView.WebhookStatus.Fail') : this.$t('Order.OrdersAll.ListView.WebhookStatus.Unknown')
      const tooltip = webhook?.Status ? webhook?.Successful ? `${webhook.CalledAt} <br> ${langObj.Name} (${webhook.Status})` : `${webhook.CalledAt} <br> ${langObj.Name} (${webhook.Status}) <br> ${webhook.Reason}` : ''

      return {
        Color  : langObj.Color,
        Icon   : langObj.Icon,
        Name   : langObj.Name,
        Tooltip: tooltip
      }
    },

    /**
     * showOrderEditDialog
     *
     * @param order {Object}
     */
    showOrderEditDialog(order) {
      this.selectedOrder = order

      this.executeWithPinPermissionManager(PinManagerEnum.PosEdit, (success) => {
        if (!success) return

        this.confirmDialogEditOrderVisible = true
      })
    },

    canEditOrder(order) {
      const sourceTypeId = parseInt(order.SourceTypeId)
      const allCatalogs = this.appConfig?.LOCATION_DATA?.Catalogs || []
      const sourceTypeExists = allCatalogs.some(catalog => catalog.SourceTypes.includes(sourceTypeId))
      const sourceTypeCanCreateOrder = allCatalogs.some(catalog => catalog.CanCreateOrder && catalog.SourceTypes.includes(sourceTypeId))
      const canCreateOrder = !sourceTypeExists || (sourceTypeExists && sourceTypeCanCreateOrder)

      return canCreateOrder && parseInt(order.StatusId) !== OrderStatusEnum.NotComplete && this.posUserCan('Orders', 'MANAGE') && this.posUserCan('Pos', 'ACCESS') && this.$route.name !== 'Pos'
    },

    /**
     * onCancelOrderClick
     *
     * @param order {Object}
     */
    onCancelOrderClick(order) {
      this.selectedOrder = order

      this.executeWithPinPermissionManager(PinManagerEnum.PosCancel, (success) => {
        if (!success) return

        this.cancelOrderConfirmDialogVisible = true
      })
    },

    /**
     * onRevertOrderClick
     *
     * @param order {Object}
     */
    onRevertOrderClick(order) {
      this.selectedOrder = order

      this.executeWithPinPermissionManager(PinManagerEnum.PosRevert, (success) => {
        if (!success) return

        this.revertOrderStatusConfirmDialogVisible = true
      })
    },

    revertOrderStatus() {
      if (!this.selectedOrder) return

      const order = clone(this.selectedOrder)
      window.callAS(window.SocketCommand.Order.RevertStatus, {OrderId: order.Id})
    },

    /**
     * onDeleteOrderClick
     *
     * @param order {Object}
     */
    onDeleteOrderClick(order) {
      if (parseInt(order.StatusId) !== OrderStatusEnum.Completed && parseInt(order.StatusId) !== OrderStatusEnum.Canceled && !this.appConfig.LOCATION_DATA.HasForceDeleteOrders) return
      this.selectedOrder = order

      this.executeWithPinPermissionManager(PinManagerEnum.PosDelete, (success) => {
        if (!success) return

        this.isDeleteAndCopyOrder = false
        this.deleteOrderConfirmDialogVisible = true
      })
    },

    /**
     * onDeleteOrdersClick
     *
     * @param orders {Array}
     */
    onDeleteOrdersClick(orders) {
      this.selectedOrder = orders?.map(o => o?.Id ?? o)

      this.executeWithPinPermissionManager(PinManagerEnum.PosDelete, (success) => {
        if (!success) return

        this.isDeleteAndCopyOrder = false
        this.deleteOrderConfirmDialogVisible = true
      })
    },

    onDeleteAllOrdersClick() {
      this.selectedOrder = null
      this.executeWithPinPermissionManager(PinManagerEnum.PosDelete, (success) => {
        if (!success) return

        this.deleteAllOrdersConfirmDialogVisible = true
      })
    },

    onEditDelete() {
      this.executeWithPinPermissionManager(PinManagerEnum.PosDelete, (success) => {
        if (!success) return

        this.isDeleteAndCopyOrder = true
        this.deleteOrderConfirmDialogVisible = true
      })
    },

    getExternalOrderRawUrl(order) {
      if (!order || !import.meta.env.VITE_API_URL || !this.authorizationToken || !this.userIsSuperAdmin) return ''

      const API_URL = import.meta.env.VITE_API_URL
      const ORDER_ID = order.Id
      const TOKEN = this.authorizationToken

      return `${API_URL}raw/order/external/${ORDER_ID}/${TOKEN}`
    },

    retryExternalOrder(order, broadcast) {
      if (!order) return

      window.callAS(window.SocketCommand.Order.RetryExternal, {
        OrderId  : order.Id,
        Broadcast: broadcast ?? false
      })
    },

    onEditAdvanced() {
      this.isDeleteAndCopyOrder = false
      window.callAS(window.SocketCommand.Order.Edit, {
        order_id  : this.selectedOrder.Id,
        catalog_id: this.selectedOrder?.ExternalCatalogId || null,
        action    : OrderEditActionEnum.EDIT,
        Reserved  : parseInt(this.posUser.staff_id)
      })
    },

    onEditCopy() {
      this.isDeleteAndCopyOrder = false
      window.callAS(window.SocketCommand.Order.Edit, {
        order_id  : this.selectedOrder.Id,
        catalog_id: this.selectedOrder?.ExternalCatalogId || null,
        //cancel    : false,
        action    : OrderEditActionEnum.COPY,
        Reserved  : parseInt(this.posUser.staff_id)
      })
    },

    onOrderEdit(data, payload) {
      if (data && data.CartId && parseInt(data.CartId) > 0) {
        this.$router.push({
          name  : 'Pos',
          params: {
            orderId             : data.CartId,
            catalogId           : payload?.catalog_id || null,
            isFromCopy          : true,
            isDeleteAndCopyOrder: this.isDeleteAndCopyOrder
          }
        })
        this.$bus.$emit('hide-order-details-dialog')
        this.$bus.$emit('hide-customer-details-dialog')
      } else {
        this.$bus.$emit('app-show-notification', {
          body   : this.$t('Pos.Pos.Notification.Error'),
          timeout: 2000,
          type   : 'error',
          icon   : 'warning'
        })
      }
    },

    getOrderDetails(orderItem) {
      if (!orderItem) return

      this.selectedOrder = orderItem

      window.callAS(window.SocketCommand.Order.GetDetails, {
        order_id : orderItem.Id,
        status_id: orderItem.StatusId
      })
    },

    updateOrderStatus(order) {
      this.$bus.$emit(window.SocketCommand.Order.UpdateStatus, order)
    },

    onOrdersDelete() {
      this.isDeleteAndCopyOrder = false
      this.selectedOrders = []
    },

    onOrderStatusUpdated(data) {
      if (data && typeof this.getData === 'function') {
        //this.getData()
      }
    },

    setOrderDetails(data) {
      if (!this.selectedOrder) return

      const payload = clone(this.selectedOrder)
      this.selectedOrder = null

      payload.Details = data
      payload.CanGenerateReceipt = data.CanGenerateReceipt
      payload.notifyDeliveryIsLoading = false
      payload.notifyStatusIsLoading = false

      this.$bus.$emit('show-order-details-dialog', payload)
    },

    downloadPdfOrdersReport(range) {
      const dateRange = range || '-'
      const token = window.authorizationToken
      const locale = getLocaleForReports()
      const url = `${import.meta.env.VITE_API_URL}pdf/order/totals/${token}/${locale}/${dateRange}`
      //console.log('downloadPdfOrdersReport', url)
      const win = window.open(url, '_blank')
      if (win) win.focus()
    },

    downloadPdfReceiptsReport(range, staffId, guid, withoutOpenHours = false) {
      const dateRange = range || '-'
      const token = window.authorizationToken
      const locale = getLocaleForReports()
      const url = `${import.meta.env.VITE_API_URL}pdf/receipt/totals/${token}/${locale}/${withoutOpenHours ?? false}/${staffId ?? null}/${guid ?? null}/${dateRange}`
      //console.log('downloadPdfReceiptsReport', url)
      const win = window.open(url, '_blank')
      if (win) win.focus()
    },

    SendEmailOrdersReport(emails, range) {
      const dateRange = range || '-'
      const payload = {Emails: emails, Range: dateRange}
      window.callAS(SocketCommand.Order.Report.EmailOrdersReport, payload)
    },

    SendEmailReceiptsReport(emails, range, staffId, guid, withoutOpenHours = false) {
      const dateRange = range || '-'
      const payload = {Emails: emails, Range: dateRange, StaffId: staffId, MachineId: guid, WithoutOpenHours: withoutOpenHours}
      window.callAS(SocketCommand.Order.Report.EmailReceiptsReport, payload)
    },

    printOrder(item, printer) {
      if (!item || !printer) return
      Printer.printOrder(item.Id, printer)
    },

    printOrderPreparation(item, printerGroup) {
      if (!item || !printerGroup) return
      Printer.printOrderPreparation(item.Id, printerGroup.Id)
    },

    printOrderTotalsZ(range, printer) {
      if (!printer) return
      Printer.printOrderTotalsZ(range, printer)
    },

    printReceiptTotalsZ(range, staffId, guid, withoutOpenHours, printer) {
      if (!printer) return
      Printer.printReceiptTotalsZ(range, staffId, guid, withoutOpenHours, printer)
    },

    issueReceiptDocumentVisible(order) {
      if (parseInt(order.SourceTypeId) === SourceTypeEnum.TABLE || parseInt(order.StatusId) === OrderStatusEnum.Canceled) return false

      return this.userCanViewFiscalDoc
    },
    printOrderDocumentVisible(order) {
      if (parseInt(order.SourceTypeId) === SourceTypeEnum.TABLE && parseInt(order.StatusId) === OrderStatusEnum.Canceled) return false

      return this.posUserHasComponentPermission('Orders', 'MANAGE') && this.posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderDocument
    },
    printOrderPreparationVisible(order) {
      if (parseInt(order.SourceTypeId) === SourceTypeEnum.TABLE && parseInt(order.StatusId) === OrderStatusEnum.Canceled) return false

      return this.posUserHasComponentPermission('Orders', 'MANAGE') && this.preparationDocumentPrintEnabled && this.posUser?.Settings?.AllUsers?.Permissions?.CanPrintOrderPreparation
    },
    printOrderMarketingVisible() {
      return this.posUserHasComponentPermission('Orders', 'MANAGE') && this.marketingDocumentPrintEnabled && this.posUser?.Settings?.AllUsers?.Permissions?.CanPrintMarketing
    },

    orderHasUnpaidBalance(order) {
      return this.orderBalance(order) > 0
    },

    orderBalance(order) {
      if (!order) return false

      const orderTotal = parseFloat(parseFloat(order.OrderTotal).toFixed(2))
      const transactionsTotal = this.orderTransactionsTotal(order)

      return orderTotal - transactionsTotal
    }
  },
  computed: {
    userCanDeleteOrders() {
      return this.posUserHasComponentPermission('Orders', 'DELETE') && this.appConfig?.LOCATION_DATA?.HasForceDeleteOrders
    },
    confirmDialogEditOrderBody() {
      return `${this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Choose')} <span class="red--text text--darken-1">${this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Delete')}</span> ${this.selectedOrder && parseInt(this.selectedOrder.SourceTypeId) !== 1 ? this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Or') + ' <span class=\'green--text text--darken-1\'>' + this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Copy') + '</span>' : ''} ${this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Order')}<br>`
    },
    confirmDialogEditOrderCanceledTitle() {
      return `${this.$t('Order.OrdersAll.ListView.Button.Edit.Title')} #   ${this.selectedOrder ? this.selectedOrder.OrderCode : ''};`
    },
    confirmDialogEditOrderCanceledBody() {
      return `${this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Choose')} <span class="green--text text--darken-1">${this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Copy')}</span> ${this.$t('Order.OrdersAll.ListView.Button.Edit.PopUp.Content.Order')}<br>`
    },

    preparationDocumentPrintEnabled() {
      return this.appConfig?.LOCATION_DATA?.PreparationDocumentEnabled || false
    },

    marketingDocumentPrintEnabled() {
      return this.appConfig?.LOCATION_DATA?.MarketingDocumentEnabled || false
    },

    sourceTypesList() {
      let sourceTypesDefault = Object.values(this.appConfig?.SOURCE_TYPES?.DEFAULT || {})
      let sourceTypesOther = this.appConfig?.SOURCE_TYPES?.OTHER || []

      sourceTypesDefault = sourceTypesDefault.filter(sourceType => sourceType.active).map(o => {
        return {
          Id         : o.source_type_id,
          Name       : this.getOrderSourceTypeLangById(o.source_type_id)?.Title || o.title,
          Description: this.getOrderSourceTypeLangById(o.source_type_id)?.Desc || o.desc,
          Avatar     : {
            Icon : this.getOrderSourceTypeLangById(o.source_type_id)?.Icon || o.logo,
            Color: 'blue-grey'
          }
        }
      })
      sourceTypesOther = sourceTypesOther.filter(sourceType => sourceType.active).map(o => {
        return {
          Id         : o.source_type_id,
          Name       : o.title,
          Description: o.desc,
          Avatar     : {
            Img  : o.logo,
            Color: 'blue-grey'
          }
        }
      })

      return [...sourceTypesDefault, ...sourceTypesOther]
    }
  },
  created() {
  },
  mounted() {
    this.$bus.$on(window.SocketCommand.Order.GetDetails, this.setOrderDetails)
    this.$bus.$on(window.SocketCommand.Order.StatusUpdated, this.onOrderStatusUpdated)
    this.$bus.$on(window.SocketCommand.Order.Edit, this.onOrderEdit)
  },
  updated() {
  },
  beforeDestroy() {
    this.$bus.$off(window.SocketCommand.Order.GetDetails, this.setOrderDetails)
    this.$bus.$off(window.SocketCommand.Order.StatusUpdated, this.onOrderStatusUpdated)
    this.$bus.$off(window.SocketCommand.Order.Edit, this.onOrderEdit)
  },
  destroyed() {
  }

}
